import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, Users, Award, CheckCircle, MapPin, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import QuoteModal from '@/components/QuoteModal';
import AnimatedCounter from '@/components/AnimatedCounter';
import Testimonials from '@/components/Testimonials';
import SEO from '@/components/SEO';

const Home = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  // Video control - loop every 6 seconds
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= 6) {
        video.currentTime = 0;
        video.play().catch(err => console.log('Video play failed:', err));
      }
    };

    const handleLoadedData = () => {
      // Try to play when loaded
      video.play().catch(err => {
        console.log('Autoplay prevented, waiting for user interaction');
      });
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadeddata', handleLoadedData);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);

  // İçeriği yükle
  useEffect(() => {
    fetchContent();
  }, []);

  // Content yüklendiğinde projeleri yükle
  useEffect(() => {
    if (content) {
      fetchProjects();
    }
  }, [content]);

  const fetchContent = async () => {
    try {
      const response = await fetch('/api/homepage-content-public');
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Content fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects-public');
      if (response.ok) {
        const data = await response.json();
        // Admin panelden seçilen projeleri kullan, yoksa varsayılan projeleri göster
        const featuredIds = content?.featured_project_ids || ['ferah-apartmani', 'emar-deluxe'];
        const featured = data
          .filter(p => featuredIds.includes(p.id))
          .map(p => ({
            id: p.id,
            name: p.name,
            location: p.location,
            year: p.year,
            image: p.images && p.images[0] ? p.images[0] : '/images/placeholder.jpg',
            status: p.status
          }));
        setProjects(featured);
      }
    } catch (error) {
      console.error('Projects fetch error:', error);
    }
  };

  // Varsayılan değerler (database'de yoksa)
  const features = [
    { 
      icon: Building2, 
      title: content?.feature1_title || 'Kalite', 
      description: content?.feature1_desc || 'En kaliteli malzemeler ve işçilikle projelerinizi hayata geçiriyoruz' 
    },
    { 
      icon: Users, 
      title: content?.feature2_title || 'Uzman Kadro', 
      description: content?.feature2_desc || '30 yılı aşkın deneyime sahip profesyonel ekibimiz' 
    },
    { 
      icon: Award, 
      title: content?.feature3_title || 'Güvenilir Hizmet', 
      description: content?.feature3_desc || 'Zamanında teslimat ve müşteri memnuniyeti odaklı çalışma' 
    },
  ];

  const stats = [
    { value: content?.stat_years || '30+', label: 'Yıllık Deneyim' },
    { value: content?.stat_projects || '25+', label: 'Tamamlanan Proje' },
    { value: content?.stat_apartments || '200+', label: 'Toplam Daire' },
    { value: content?.stat_clients || '500+', label: 'Mutlu Müşteri' },
  ];

  const contactInfo = [
    { icon: MapPin, title: 'Adres', content: 'Merkez Mahallesi Güngören Caddesi No:47 Bağcılar/ İSTANBUL' },
    { icon: Phone, title: 'Telefon', content: '0 (212) 433 01 48', link: 'tel:+902124330148' },
    { icon: Mail, title: 'Email', content: 'info@migayapi.com', link: 'mailto:info@migayapi.com' },
  ];

  return (
    <>
      <SEO 
        title="MİGA Yapı"
        description="30 yılı aşkın deneyimimizle İstanbul ve Kocaeli'de kentsel dönüşüm, yeni inşaat ve tadilat hizmetleri sunuyoruz. Güvenilir, kaliteli ve zamanında teslimat."
        keywords="inşaat firması istanbul, kentsel dönüşüm, miga yapı, inşaat şirketi, yeni inşaat, tadilat, güvenilir inşaat, bahçelievler inşaat, bağcılar inşaat"
      />
      <div data-testid="home-page" className="min-h-screen">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          {/* Video Background - Desktop only */}
          <div className="absolute inset-0 z-0">
            {/* High Quality Video for desktop */}
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              preload="auto"
              className="hidden md:block absolute inset-0 w-full h-full object-cover"
              style={{ 
                filter: 'brightness(0.85) contrast(1.15) saturate(1.15)',
                imageRendering: 'high-quality',
                transform: 'translateZ(0)',
                willChange: 'transform',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
              poster="https://customer-assets.emergentagent.com/job_modern-build-2/artifacts/tijrhuja_wide15.jpg"
            >
              <source src="/api/../static/videos/hero-background.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Static image for mobile (better performance) */}
            <div 
              className="md:hidden absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_modern-build-2/artifacts/tijrhuja_wide15.jpg)',
                filter: 'brightness(0.7)'
              }}
            ></div>
            
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              data-testid="hero-title"
              className="heading-font text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up drop-shadow-2xl"
            >
              {content?.hero_slogan || 'Hayallerinizi Güvenle İnşa Ediyoruz'}
            </h1>
            <p
              data-testid="hero-description"
              className="text-xl sm:text-2xl text-white/90 mb-8 max-w-3xl mx-auto animate-fade-in-up drop-shadow-lg"
              style={{ animationDelay: '0.2s' }}
            >
              {content?.hero_description || '30 yılı aşkın sektör tecrübemiz ve 25\'ten fazla başarılı projemizle İstanbul\'da kaliteli inşaat hizmetleri sunuyoruz'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Button
                onClick={() => setShowQuoteModal(true)}
                data-testid="hero-quote-button"
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 text-lg rounded-full font-medium transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
              >
                Ücretsiz Teklif Alın
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                asChild
                data-testid="hero-projects-button"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#1e4a7a] px-8 py-6 text-lg rounded-full font-medium transition-all duration-300"
              >
                <Link to="/projelerimiz">Projelerimizi İnceleyin</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                data-testid="features-title"
                className="heading-font text-4xl sm:text-5xl font-bold text-[#1e4a7a] mb-4"
              >
                {content?.why_section_title || 'Neden MİGA Yapı?'}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {content?.why_section_description || 'Sektördeki uzmanlığımız ve müşteri memnuniyeti odaklı yaklaşımımızla fark yaratıyoruz'}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-20">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    data-testid={`feature-card-${index}`}
                    className={`group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-[#dc2626] transition-all duration-300 hover-lift stagger-${index + 1}`}
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1e4a7a] to-[#2563eb] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="heading-font text-2xl font-semibold text-[#1e4a7a] mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Stats */}
            <div className="bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-12">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center text-white">
                {stats.map((stat, index) => (
                  <div key={index} data-testid={`stat-${index}`} className="stat-animate">
                    <div className="heading-font text-5xl font-bold mb-2">
                      <AnimatedCounter end={stat.value} suffix={stat.value.includes('+') ? '+' : ''} />
                    </div>
                    <div className="text-lg">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Projects Preview Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                data-testid="projects-preview-title"
                className="heading-font text-4xl sm:text-5xl font-bold text-[#1e4a7a] mb-4"
              >
                Projelerimiz
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Kaliteli işçilik ve modern teknolojilerle hayata geçirdiğimiz projelerimiz
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {projects.map((project, index) => (
                <Link
                  key={index}
                  to={`/projelerimiz/${project.id}`}
                  data-testid={`project-preview-${index}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover-lift block cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <div className="text-white text-sm">
                        <div className="flex items-center mb-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          <span>{project.location}</span>
                        </div>
                        <div className="text-xs opacity-90">{project.year}</div>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                        project.status === 'Devam Ediyor' 
                          ? 'bg-blue-500/90 text-white' 
                          : project.status === 'Tamamlandı'
                          ? 'bg-green-500/90 text-white'
                          : 'bg-amber-500/90 text-white'
                      }`}>
                        {project.status}
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="heading-font text-lg font-bold text-[#1e4a7a] mb-2 group-hover:text-[#dc2626] transition-colors line-clamp-2">
                      {project.name}
                    </h3>
                    <div className="flex items-center justify-between text-gray-500 text-sm">
                      <span className="group-hover:text-[#1e4a7a] transition-colors">Detayları Gör</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Button
                asChild
                data-testid="view-all-projects-button"
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 text-lg rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Link to="/projelerimiz">
                  Tüm Projeleri Görüntüle
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <Testimonials />

        {/* Contact Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                data-testid="contact-section-title"
                className="heading-font text-4xl sm:text-5xl font-bold text-[#1e4a7a] mb-4"
              >
                İletişim
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Projeleriniz için bizimle iletişime geçin
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div
                    key={index}
                    data-testid={`contact-info-${index}`}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center border border-gray-100"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1e4a7a] to-[#2563eb] rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="heading-font text-xl font-semibold text-[#1e4a7a] mb-3">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-gray-600 hover:text-[#dc2626] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-600">{info.content}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-[#1e4a7a] to-[#2563eb]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              data-testid="cta-title"
              className="heading-font text-4xl sm:text-5xl font-bold text-white mb-6"
            >
              Projenize Hemen Başlayalım
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Tapu belgenizi yükleyerek ücretsiz ve detaylı teklif alabilirsiniz
            </p>
            <Button
              onClick={() => setShowQuoteModal(true)}
              data-testid="cta-quote-button"
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 text-lg rounded-full font-medium transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              Teklif Al
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </div>

      <QuoteModal open={showQuoteModal} onOpenChange={setShowQuoteModal} />
    </>
  );
};

export default Home;